import React, { useState, useEffect } from "react";
import { Card, Row, Col, Button, Spin, Table, Input, Tag } from "antd";
import { useSelector } from "react-redux";
import {
  getSmtpPaginated,
  deleteSmtpById,
  updateSmtpHealthCheck,
} from "@api/email/users/smtp";
import { EmailNotificationModal } from "@components";

const { Search } = Input;

const SmtpList = ({ history }) => {
  const { user } = useSelector((state) => state.auth);

  // State variables
  const [smtpServers, setSmtpServers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    totalSmtpServers: 0,
  });

  // Modal states
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedServerId, setSelectedServerId] = useState(null);
  const [healthCheckSuccess, setHealthCheckSuccess] = useState(false);
  const [errorModalVisible, setErrorModalVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Fetch SMTP servers on component mount and when pagination/search changes
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const page = parseInt(searchParams.get("page") || "1", 10);
    const pageSize = parseInt(searchParams.get("pageSize") || "10", 10);
    const search = searchParams.get("search") || "";

    setSearchText(search);
    setPagination({ current: page, pageSize, totalSmtpServers: 0 });
    fetchSmtpServers(page, pageSize, search);
  }, []);

  // Function to fetch SMTP servers
  const fetchSmtpServers = async (page, pageSize, searchText = "") => {
    setLoading(true);
    try {
      const res = await getSmtpPaginated(page, pageSize, searchText);
      setSmtpServers(res.data.smtpServers || []);
      setPagination((p) => ({
        ...p,
        totalSmtpServers: res.data.paginationData?.totalSmtpServers,
        current: res.data.paginationData?.page,
        pageSize: res.data.paginationData?.perPage,
      }));
    } catch (err) {
      console.error("Fetch SMTP servers error", err);
      showErrorModal("Failed to fetch SMTP servers. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle pagination change
  const handlePaginationChange = (page, pageSize) => {
    const searchParams = new URLSearchParams();
    searchParams.set("page", page);
    searchParams.set("pageSize", pageSize);
    if (searchText) searchParams.set("search", searchText);

    history.push({ search: searchParams.toString() });
    fetchSmtpServers(page, pageSize, searchText);
  };

  // Handle search
  const handleSearch = (searchValue) => {
    setSearchText(searchValue);
    setPagination({ ...pagination, current: 1 }); // Reset to first page
    fetchSmtpServers(1, pagination.pageSize, searchValue);

    const searchParams = new URLSearchParams();
    searchParams.set("page", "1");
    searchParams.set("pageSize", pagination.pageSize);
    if (searchValue) searchParams.set("search", searchValue);
    history.push({ search: searchParams.toString() });
  };

  // Function to handle SMTP server update
  const handleSmtpUpdate = (id) => {
    history.push(`/super-admin/email/smtp/${id}/update`);
  };

  // Function to handle SMTP server delete
  const handleSmtpDelete = (id) => {
    setSelectedServerId(id);
    setDeleteModalVisible(true);
  };

  // Function to confirm deletion of SMTP server
  const confirmSmtpDelete = async () => {
    try {
      await deleteSmtpById(selectedServerId);
      fetchSmtpServers(pagination.current, pagination.pageSize);
      showSuccessModal("SMTP server deleted successfully");
    } catch (error) {
      console.error("Error deleting SMTP server", error);
      showErrorModal("Failed to delete SMTP server. Please try again.");
    }
  };

  // Function to handle health check
  const handleHealthCheck = async (id) => {
    try {
      await updateSmtpHealthCheck(id);
      setHealthCheckSuccess(true);
      fetchSmtpServers(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error("Error updating health check", error);
      showErrorModal("Failed to perform health check. Please try again.");
    }
  };

  // Function to show error modal
  const showErrorModal = (message) => {
    setErrorMessage(message);
    setErrorModalVisible(true);
  };

  // Function to show success modal
  const showSuccessModal = (message) => {
    // You can implement a success modal state here if needed
  };

  // Table columns configuration
  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Host",
      dataIndex: "host",
      key: "host",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag
          color={
            status === "active"
              ? "green"
              : status === "inactive"
              ? "orange"
              : "red"
          }
        >
          {status}
        </Tag>
      ),
    },
    {
      title: "Current Load",
      key: "load",
      render: (_, record) =>
        `${((record.currentLoad / record.maxQuota) * 100).toFixed(2)}%`,
    },
    {
      title: "Last Health Check",
      dataIndex: "lastHealthCheck",
      key: "lastHealthCheck",
      render: (date) => (date ? new Date(date).toLocaleString() : "Never"),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <>
          <Button
            type="primary"
            size="small"
            style={{ marginRight: "10px" }}
            onClick={() => handleSmtpUpdate(record._id)}
          >
            Edit
          </Button>
          <Button
            type="default"
            size="small"
            style={{ marginRight: "10px" }}
            onClick={() => handleHealthCheck(record._id)}
          >
            Health Check
          </Button>
          <Button
            type="danger"
            size="small"
            onClick={() => handleSmtpDelete(record._id)}
          >
            Delete
          </Button>
        </>
      ),
    },
  ];

  return (
    <Card style={{ borderRadius: "0", height: "100vh", overflowY: "hidden" }}>
      <Row justify="space-between" style={{ marginBottom: 16 }}>
        <Col>
          <h3>SMTP Servers List</h3>
        </Col>
        <Col>
          {/* Create SMTP Server Button */}
          <Button
            type="primary"
            style={{ marginRight: 10 }}
            onClick={() => history.push("/super-admin/email/smtp/create")}
          >
            Add SMTP Server
          </Button>

          {/* View User Emails */}
          <Button
            type="primary"
            style={{ marginRight: 10 }}
            onClick={() => history.push("/super-admin/email/users")}
          >
            View User Emails
          </Button>

          {/* Search */}
          <Search
            placeholder="Search by name/host"
            style={{ width: "300px", marginRight: 10 }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={handleSearch}
          />
        </Col>
      </Row>
      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={smtpServers}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.totalSmtpServers,
          }}
          loading={loading}
          onChange={({ current, pageSize }) =>
            handlePaginationChange(current, pageSize)
          }
          rowKey={(record) => record._id}
          scroll={{ y: "calc(100vh - 240px)" }}
          tableLayout="fixed"
        />
      </Spin>

      {/* Delete Confirmation Modal */}
      <EmailNotificationModal
        isOpen={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        type="delete"
        title="Delete SMTP Server"
        message="Are you sure you want to delete this SMTP server? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmSmtpDelete}
      />

      {/* Health Check Success Modal */}
      <EmailNotificationModal
        isOpen={healthCheckSuccess}
        onClose={() => setHealthCheckSuccess(false)}
        type="success"
        title="Health Check Complete"
        message="The SMTP server health check has been completed successfully."
        autoCloseTime={3000}
      />

      {/* Error Modal */}
      <EmailNotificationModal
        isOpen={errorModalVisible}
        onClose={() => setErrorModalVisible(false)}
        type="error"
        title="Operation Failed"
        message={errorMessage}
      />
    </Card>
  );
};

export default SmtpList;
